import { useEffect, useRef, useState } from 'react';
import { useLibraryIdentity } from '@/hooks/useLibraryIdentity';
import { useNutritionGoalsActionConfirm } from '@/hooks/useNutritionGoalsActionConfirm';
import { notifyNutritionChanged } from '@/hooks/useNutritionResource';
import { useLanguage } from '@/i18n/LanguageProvider';
import { NutritionGoalsRequestSummary } from '@/components/mirror/NutritionGoalsActionModal';
import {
  NUTRITION_GOALS_PENDING_CHANGED, nutritionGoalsConfirmedReceipt, nutritionGoalsReviewedRequestsEqual,
  type NutritionGoalsConfirmedReceipt, type NutritionGoalsWriteRequest,
} from '@/lib/nutritionGoalsExecutor';
import { parseNutritionGoalsActionRequest } from '../../../supabase/functions/_shared/nutritionGoalsActionDomain';

/** Owner recovery remains reachable without the original message or conversation. */
export default function NutritionGoalsPendingRecoveryPanel({ refreshKey = '' }: { refreshKey?: string }) {
  const identity = useLibraryIdentity();
  if (!identity.ready || !identity.userId) return null;
  return <OwnerGoalsRecovery key={[identity.userId, identity.generation].join(':')} ownerId={identity.userId} refreshKey={refreshKey} />;
}
function OwnerGoalsRecovery({ ownerId, refreshKey }: { ownerId: string; refreshKey: string }) {
  const { t } = useLanguage(), [refresh, setRefresh] = useState(0), busy = useRef(false);
  const write = useNutritionGoalsActionConfirm(ownerId, () => true, refresh);
  const [receipt, setReceipt] = useState<NutritionGoalsConfirmedReceipt | null>(null);
  const [error, setError] = useState<string | null>(null), committed = useRef<string | null>(null);
  const previousTransition = useRef(refreshKey), hasPending = useRef(false); hasPending.current = !!write.pending;
  useEffect(() => {
    const changed = () => { if (!busy.current) { setReceipt(null); setError(null); setRefresh(value => value + 1); } };
    window.addEventListener(NUTRITION_GOALS_PENDING_CHANGED, changed);
    window.addEventListener('focus', changed);
    return () => { window.removeEventListener(NUTRITION_GOALS_PENDING_CHANGED, changed); window.removeEventListener('focus', changed); };
  }, []);
  useEffect(() => {
    if (previousTransition.current === refreshKey) return;
    previousTransition.current = refreshKey;
    // A changed conversation discovers a newly persisted attempt but never
    // rebinds an already loaded historical proof to that conversation.
    if (!busy.current && !hasPending.current) setRefresh(value => value + 1);
  }, [refreshKey]);
  let request: NutritionGoalsWriteRequest | null = null;
  try { if (write.pending) request = parseNutritionGoalsActionRequest(write.pending.body, { historical: true }) as NutritionGoalsWriteRequest; } catch { /* Corrupt journals remain blocked. */ }
  const proof = write.ready && request && receipt && nutritionGoalsReviewedRequestsEqual(request, receipt.request) ? receipt : null;
  const signature = proof ? proof.operation_id + ':' + proof.receipt_id : null;
  useEffect(() => { committed.current = signature; return () => { committed.current = null; }; }, [signature]);
  const recover = async () => {
    if (busy.current || !write.ready || !write.pending) return;
    const assert = write.captureCurrent(); busy.current = true; setError(null);
    try {
      const done = await write.recover(); assert();
      if (done) { const verified = nutritionGoalsConfirmedReceipt(done.entry, done.response); if (!verified) throw new Error('outcome_unknown'); setReceipt(verified); }
    } catch (cause) { try { assert(); setError((cause as Error).message); } catch { /* Retired owner or auth session. */ } }
    finally { busy.current = false; }
  };
  const acknowledge = async () => {
    if (busy.current || !proof || !signature || committed.current !== signature) return;
    const assert = write.captureCurrent(); busy.current = true; setError(null);
    try {
      // This acknowledges the visible card committed in this owner surface.
      // It neither creates a chat message nor claims durable chat persistence.
      assert(); if (!await write.acknowledge()) return; assert(); setReceipt(null); notifyNutritionChanged();
    } catch (cause) { try { assert(); setError((cause as Error).message); } catch { /* Retired owner. */ } }
    finally { busy.current = false; }
  };
  const release = async () => {
    if (busy.current || !write.pending) return;
    const assert = write.captureCurrent(); busy.current = true;
    try { if (await write.releaseFailure()) { assert(); setReceipt(null); setError(null); } }
    finally { busy.current = false; }
  };
  const invalidPending = !!write.pending && !request;
  const raw = error ?? write.error ?? (invalidPending ? 'pending_storage_unavailable' : null), failed = raw === 'nutrition_goals_failed';
  if (!write.pending && !raw) return null;
  return <section className="min-w-0 space-y-3 rounded-xl border border-border bg-background p-3" aria-label={t('nutritionGoalsAction.title')}>
    <h2 className="font-semibold">{t('nutritionGoalsAction.title')}</h2>
    <p className="text-sm">{t('nutritionGoalsAction.ownerRecoveryIntro')}</p>
    {raw && <p role="alert">{t(raw === 'invalid_session' ? 'nutritionGoalsAction.accountChanged' : failed ? 'nutritionGoalsAction.terminalFailed' : 'nutritionGoalsAction.outcomeUnknown')}</p>}
    {request && <NutritionGoalsRequestSummary request={request} />}
    {proof && <p role="status">{t('nutritionGoalsAction.ownerRecoverySaved')}</p>}
    {!proof && !failed && !invalidPending && write.pending && <button type="button" className="min-h-11 rounded-xl border border-border px-3 py-2 text-sm disabled:opacity-50" disabled={!write.ready || write.busy} onClick={() => void recover()}>{t('nutritionGoalsAction.recover')}</button>}
    {proof && <button type="button" className="min-h-11 rounded-xl bg-primary px-3 py-2 text-sm text-primary-foreground disabled:opacity-50" disabled={!write.ready || write.busy} onClick={() => void acknowledge()}>{t('nutritionGoalsAction.ownerRecoveryAcknowledge')}</button>}
    {failed && write.pending && <button type="button" className="min-h-11 rounded-xl border border-border px-3 py-2 text-sm disabled:opacity-50" disabled={!write.ready || write.busy} onClick={() => void release()}>{t('nutritionGoalsAction.releaseFailed')}</button>}
  </section>;
}
